import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../../config/axios';
import { useAuth } from '../../../context/AuthContext';
import { Button, Display, Meta } from '../../components/Atoms';
import { LoadingState, ErrorState } from '../../components/states';
import { useApi } from '../../lib/api';
import ProfileShell, { ProfileLabel, ProfileSection, profileInputStyle } from './ProfileShell';

/**
 * ProfileAccount - email, display_name, hasło, usuwanie konta.
 *
 * Endpointy:
 *   GET  /api/auth/me           - basic user info
 *   PUT  /api/auth/profile      - display_name + inne whitelisted fields
 *   PUT  /api/auth/email        - zmiana emaila (z password re-auth + verification mail)
 *   POST /api/auth/forgot-password — flow zmiany hasła (workaround, brak osobnego)
 *   DELETE /api/auth/account    - soft delete (30-dniowe okno na restore)
 */

function ShareGardenToggle({ user }) {
  const [val, setVal] = useState(user?.share_garden_with_friends !== false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const toggle = async () => {
    if (saving) return;
    setSaving(true); setErr(''); setMsg('');
    const newVal = !val;
    try {
      await axios.put('/api/auth/profile', { share_garden_with_friends: newVal ? 1 : 0 });
      setVal(newVal);
      setMsg(newVal ? 'Zapisane — znajomi widzą Twój ogród.' : 'Zapisane — ogród ukryty.');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setErr(e.response?.data?.error || 'Nie udało się zapisać zmian. Spróbuj ponownie.');
      setVal(!newVal); // revert
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '14px 16px', borderRadius: 12, background: 'var(--t-bg-elev)', border: '1px solid var(--t-line)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, color: 'var(--t-text)', fontWeight: 500 }}>
            {val ? 'Ogród widoczny dla znajomych' : 'Ogród prywatny'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--t-text-dim)', marginTop: 4 }}>
            {val ? 'Znajomi mogą przeglądać Twoje poletka i grządki (read-only).' : 'Nikt nie zobaczy Twojego ogrodu.'}
          </div>
          {msg && <div style={{ fontSize: 11, color: 'var(--t-sage)', marginTop: 6 }}>{msg}</div>}
          {err && <div style={{ fontSize: 11, color: 'var(--t-rust)', marginTop: 6 }}>{err}</div>}
        </div>
        <button
          type="button"
          onClick={toggle}
          disabled={saving}
          style={{
            flexShrink: 0,
            width: 48, height: 26, borderRadius: 13,
            background: val ? 'var(--t-sage)' : 'var(--t-line-2)',
            border: 'none', position: 'relative', cursor: saving ? 'not-allowed' : 'pointer',
            transition: 'background 200ms ease',
          }}
          aria-pressed={val}
          aria-label="Przełącz widoczność ogrodu"
        >
          <span style={{
            position: 'absolute', top: 3, left: 3,
            width: 20, height: 20, borderRadius: '50%',
            background: '#fff',
            transform: val ? 'translateX(22px)' : 'translateX(0)',
            transition: 'transform 200ms ease',
          }} />
        </button>
      </div>
    </div>
  );
}

export default function ProfileAccount() {
  const { data: meRes, loading, error, refetch } = useApi('/api/auth/me');
  const { logout } = useAuth();
  const navigate = useNavigate();

  // Display name editor
  const [displayName, setDisplayName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameMsg, setNameMsg] = useState('');
  const [nameError, setNameError] = useState('');

  // Email change modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailPwd, setEmailPwd] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailMsg, setEmailMsg] = useState('');
  const [emailError, setEmailError] = useState('');

  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const user = meRes?.user || {};

  useEffect(() => {
    if (user?.display_name != null) setDisplayName(user.display_name || '');
  }, [user?.display_name]);

  if (error?.status === 401) {
    return <ProfileShell><ErrorState error={error} /></ProfileShell>;
  }
  if (loading) return <ProfileShell><LoadingState /></ProfileShell>;

  const handleSaveDisplayName = async (e) => {
    e?.preventDefault?.();
    if (savingName) return;
    setSavingName(true); setNameError(''); setNameMsg('');
    try {
      await axios.put('/api/auth/profile', { display_name: displayName.trim() });
      setNameMsg('Zapisane.');
      refetch?.();
      setTimeout(() => setNameMsg(''), 2500);
    } catch (err) {
      setNameError(err.response?.data?.error || 'Nie udało się zapisać.');
    } finally {
      setSavingName(false);
    }
  };

  const handleSubmitEmail = async (e) => {
    e?.preventDefault?.();
    if (savingEmail) return;
    if (!emailPwd || !newEmail) {
      setEmailError('Wpisz aktualne hasło i nowy email.');
      return;
    }
    setSavingEmail(true); setEmailError(''); setEmailMsg('');
    try {
      const res = await axios.put('/api/auth/email', { password: emailPwd, newEmail: newEmail.trim() });
      setEmailMsg(res.data?.message || 'Email zmieniony — sprawdź skrzynkę.');
      setEmailPwd(''); setNewEmail('');
      refetch?.();
      setTimeout(() => {
        setShowEmailModal(false);
        setEmailMsg('');
      }, 3000);
    } catch (err) {
      setEmailError(err.response?.data?.error || 'Nie udało się zmienić emaila.');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'USUWAM') {
      setDeleteError('Wpisz słowo USUWAM żeby potwierdzić.');
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      await axios.delete('/api/auth/account', { data: { password: deletePassword } });
      await logout();
      navigate('/login?deleted=1');
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'Nie udało się usunąć konta.');
      setDeleting(false);
    }
  };

  return (
    <ProfileShell
      eyebrow="Konto"
      title="Email, hasło i bezpieczeństwo"
      subtitle="Tutaj zarządzasz dostępem do swojego konta."
    >
      {/* Display name — edycja inline */}
      <ProfileSection
        title="Wyświetlana nazwa"
        description="Tak będziesz widoczny w aplikacji (np. w Witaj). Możesz zostawić puste — wtedy używamy nazwy użytkownika."
      >
        <form onSubmit={handleSaveDisplayName}>
          <ProfileLabel htmlFor="dn">Imię / pseudonim</ProfileLabel>
          <input
            id="dn"
            type="text"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            placeholder={user.username || 'Twoje imię'}
            maxLength={60}
            style={profileInputStyle}
          />
          {nameError && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--t-rust)' }}>{nameError}</div>
          )}
          {nameMsg && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--t-sage)' }}>{nameMsg}</div>
          )}
          <div style={{ marginTop: 12 }}>
            <Button type="submit" disabled={savingName}>
              {savingName ? 'Zapisuję…' : 'Zapisz'}
            </Button>
          </div>
        </form>
      </ProfileSection>

      <ProfileSection
        title="Email i logowanie"
        description="Email jest twoim głównym sposobem logowania. Po zmianie wysyłamy link weryfikacyjny."
      >
        <div style={{ marginBottom: 14 }}>
          <ProfileLabel>Aktualny email</ProfileLabel>
          <div style={{
            padding: '14px 16px', borderRadius: 12,
            background: 'var(--t-bg-elev)',
            border: '1px solid var(--t-line)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            gap: 12,
          }}>
            <span style={{ color: 'var(--t-text)', fontSize: 14, wordBreak: 'break-all' }}>
              {user.email}
            </span>
            {user.email_verified ? (
              <span style={{
                fontFamily: 'var(--t-mono)', fontSize: 10,
                letterSpacing: '.14em', textTransform: 'uppercase',
                padding: '4px 8px', borderRadius: 4,
                background: 'rgba(168, 196, 158, 0.15)',
                color: 'var(--t-sage)',
                whiteSpace: 'nowrap',
              }}>✓ ZWERYFIKOWANY</span>
            ) : (
              <span style={{
                fontFamily: 'var(--t-mono)', fontSize: 10,
                letterSpacing: '.14em', textTransform: 'uppercase',
                padding: '4px 8px', borderRadius: 4,
                background: 'rgba(220, 180, 100, 0.15)',
                color: 'var(--t-amber)',
                whiteSpace: 'nowrap',
              }}>⚠ NIEZWERYFIKOWANY</span>
            )}
          </div>
        </div>

        <Button variant="ghost" onClick={() => { setShowEmailModal(true); setEmailError(''); setEmailMsg(''); }}>
          Zmień email
        </Button>

        <div style={{ marginTop: 18 }}>
          <ProfileLabel>Nazwa użytkownika</ProfileLabel>
          <div style={{
            padding: '14px 16px', borderRadius: 12,
            background: 'var(--t-bg-elev)',
            border: '1px solid var(--t-line)',
            color: 'var(--t-text)', fontSize: 14,
          }}>{user.username}</div>
          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--t-text-mute)' }}>
            Nazwa użytkownika jest stała — żeby ją zmienić, skontaktuj się z administratorem.
          </div>
        </div>

        <div style={{ marginTop: 18 }}>
          <ProfileLabel>Konto utworzone</ProfileLabel>
          <div style={{
            padding: '14px 16px', borderRadius: 12,
            background: 'var(--t-bg-elev)',
            border: '1px solid var(--t-line)',
            color: 'var(--t-text-dim)', fontSize: 14,
            fontFamily: 'var(--t-mono)', letterSpacing: '.04em',
          }}>{user.created_at ? new Date(user.created_at).toLocaleDateString('pl-PL') : '—'}</div>
        </div>
      </ProfileSection>

      <ProfileSection
        title="Hasło"
        description="Żeby zmienić hasło, wyślij sobie link resetujący na email."
      >
        <Button
          variant="ghost"
          onClick={() => navigate('/forgot-password')}
        >Wyślij link do zmiany hasła</Button>
      </ProfileSection>

      <ProfileSection
        title="Powiadomienia push"
        description="Natychmiastowe powiadomienia na telefon. Wymaga zgody na powiadomienia w przeglądarce."
      >
        <PushToggle />
      </ProfileSection>

      <ProfileSection
        title="Prywatność — ogród dla znajomych"
        description="Gdy włączone, Twoi znajomi mogą zobaczyć Twoje poletka, grządki i galerię (read-only). Wyłączenie ukrywa ogród — nawet dla znajomych."
      >
        <ShareGardenToggle user={user} />
      </ProfileSection>

      <ProfileSection
        title="Powiadomienia email"
        description="Wybierz jakie maile chcesz dostawać. Maile transakcyjne (potwierdzenia, reset hasła, weryfikacja) są zawsze wysyłane — nie podlegają tym ustawieniom."
      >
        <NotificationPrefs />
      </ProfileSection>

      <ProfileSection
        title="Zablokowani użytkownicy"
        description="Zablokowani nie mogą wysłać Ci zaproszenia, komentować ani widzieć Twojej aktywności."
      >
        <BlockedUsers />
      </ProfileSection>

      <ProfileSection
        title="Wyloguj"
        description="Wyloguj się z tego urządzenia."
      >
        <Button
          variant="ghost"
          onClick={async () => {
            await logout();
            navigate('/login');
          }}
        >Wyloguj się</Button>
      </ProfileSection>

      {/* Danger zone - usuwanie konta */}
      <ProfileSection
        title="Strefa niebezpieczna"
        description="Usunięcie konta jest soft delete — masz 30 dni na cofnięcie z linka emailowego. Po tym czasie wszystkie dane (działki, grządki, zdjęcia, notatki) są usuwane bezpowrotnie."
      >
        {!showDeleteConfirm ? (
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            style={{
              padding: '12px 18px', borderRadius: 10,
              background: 'transparent',
              border: '1px solid var(--t-rust)',
              color: 'var(--t-rust)',
              fontFamily: 'var(--t-sans)', fontSize: 14,
              fontWeight: 500, cursor: 'pointer',
            }}
          >Usuń moje konto</button>
        ) : (
          <div style={{
            padding: '20px 22px', borderRadius: 14,
            background: 'rgba(192, 106, 74, 0.06)',
            border: '1px solid var(--t-rust)',
          }}>
            <Display size={18} style={{ display: 'block', marginBottom: 8, color: 'var(--t-rust)' }}>
              Czy na pewno?
            </Display>
            <div style={{
              fontSize: 13, color: 'var(--t-text-dim)',
              lineHeight: 1.5, marginBottom: 18,
            }}>
              Wyślemy email z linkiem cofnięcia (30 dni). Twoje dane zostaną oznaczone
              do usunięcia. Po 30 dniach — bezpowrotnie zniszczone.
            </div>

            {deleteError && (
              <div style={{
                padding: '10px 14px', borderRadius: 8,
                background: 'rgba(192, 106, 74, 0.1)',
                fontSize: 12, color: 'var(--t-text)', marginBottom: 14,
              }}>{deleteError}</div>
            )}

            <div style={{ marginBottom: 14 }}>
              <ProfileLabel htmlFor="delpwd">Aktualne hasło</ProfileLabel>
              <input
                id="delpwd"
                type="password"
                value={deletePassword}
                onChange={e => setDeletePassword(e.target.value)}
                style={profileInputStyle}
              />
            </div>

            <div style={{ marginBottom: 18 }}>
              <ProfileLabel htmlFor="delconfirm">Wpisz USUWAM żeby potwierdzić</ProfileLabel>
              <input
                id="delconfirm"
                type="text"
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder="USUWAM"
                style={profileInputStyle}
              />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={deleting || deleteConfirmText !== 'USUWAM' || !deletePassword}
                style={{
                  padding: '12px 18px', borderRadius: 10,
                  background: deleteConfirmText === 'USUWAM' && deletePassword
                    ? 'var(--t-rust)' : 'var(--t-line-2)',
                  border: 'none',
                  color: 'var(--t-bg)',
                  fontFamily: 'var(--t-sans)', fontSize: 14,
                  fontWeight: 500,
                  cursor: deleteConfirmText === 'USUWAM' && deletePassword
                    ? 'pointer' : 'not-allowed',
                  opacity: deleting ? 0.6 : 1,
                }}
              >{deleting ? 'Usuwam…' : 'Tak, usuń konto'}</button>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletePassword('');
                  setDeleteConfirmText('');
                  setDeleteError('');
                }}
              >Anuluj</Button>
            </div>
          </div>
        )}
      </ProfileSection>

      {/* Modal: zmiana emaila */}
      {showEmailModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Zmień email"
          onClick={(e) => { if (e.target === e.currentTarget) setShowEmailModal(false); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.6)',
            display: 'grid', placeItems: 'center',
            padding: 16,
          }}
        >
          <div style={{
            width: '100%', maxWidth: 420,
            background: 'var(--t-bg-elev)',
            border: '1px solid var(--t-line-2)',
            borderRadius: 16,
            padding: 24,
            position: 'relative',
          }}>
            {/* NAV3 — widoczny X (iPad nie ma ESC; klik-w-tło nieodkrywalny) */}
            <button
              type="button"
              aria-label="Zamknij"
              onClick={() => setShowEmailModal(false)}
              style={{
                position: 'absolute', top: 8, right: 8,
                width: 44, height: 44, borderRadius: 12,
                display: 'grid', placeItems: 'center',
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--t-text-mute)', fontSize: 22, lineHeight: 1,
              }}
            >×</button>
            <Display size={22} style={{ display: 'block', marginBottom: 6, letterSpacing: '-.02em', paddingRight: 40 }}>
              Zmień email
            </Display>
            <div style={{ fontSize: 13, color: 'var(--t-text-dim)', lineHeight: 1.55, marginBottom: 18 }}>
              Wpisz aktualne hasło i nowy adres. Wyślemy link weryfikacyjny.
              Do potwierdzenia będziesz logować się starym emailem aż do kliknięcia w link.
            </div>

            <form onSubmit={handleSubmitEmail}>
              <div style={{ marginBottom: 14 }}>
                <ProfileLabel htmlFor="empwd">Aktualne hasło</ProfileLabel>
                <input
                  id="empwd"
                  type="password"
                  autoComplete="current-password"
                  value={emailPwd}
                  onChange={e => setEmailPwd(e.target.value)}
                  style={profileInputStyle}
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <ProfileLabel htmlFor="emnew">Nowy email</ProfileLabel>
                <input
                  id="emnew"
                  type="email"
                  autoComplete="email"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  placeholder="np. nowy@example.com"
                  style={profileInputStyle}
                />
              </div>

              {emailError && (
                <div style={{
                  marginBottom: 12, padding: '10px 12px', borderRadius: 8,
                  background: 'rgba(192, 106, 74, 0.10)',
                  color: 'var(--t-rust)', fontSize: 12,
                }}>{emailError}</div>
              )}
              {emailMsg && (
                <div style={{
                  marginBottom: 12, padding: '10px 12px', borderRadius: 8,
                  background: 'rgba(168, 196, 158, 0.10)',
                  color: 'var(--t-sage)', fontSize: 12,
                }}>{emailMsg}</div>
              )}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <Button type="button" variant="ghost" onClick={() => setShowEmailModal(false)}>
                  Anuluj
                </Button>
                <Button type="submit" disabled={savingEmail}>
                  {savingEmail ? 'Zapisuję…' : 'Zmień email'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </ProfileShell>
  );
}

// ── PushToggle — sekcja power notifications (T4) ─────────────────
function PushToggle() {
  const [pushSupported, setPushSupported] = useState(null); // null = loading
  const [pushEnabled, setPushEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setPushSupported(supported);
    if (!supported) return;

    navigator.serviceWorker.ready.then(reg =>
      reg.pushManager.getSubscription()
    ).then(sub => {
      setPushEnabled(!!sub);
    }).catch(() => {});
  }, []);

  const handleToggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (pushEnabled) {
        const { unregisterPushSubscription } = await import('../../lib/push');
        await unregisterPushSubscription();
        setPushEnabled(false);
      } else {
        const { registerPushSubscription } = await import('../../lib/push');
        const ok = await registerPushSubscription();
        setPushEnabled(ok);
      }
    } catch (e) {
      console.warn('Push toggle error:', e);
    } finally {
      setBusy(false);
    }
  };

  if (pushSupported === null) return null;
  if (!pushSupported) {
    return (
      <div style={{
        fontSize: 13, color: 'var(--t-text-dim)', padding: '12px 0',
      }}>
        Twoja przeglądarka nie obsługuje powiadomień push.
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '12px 0',
    }}>
      <button
        type="button"
        onClick={handleToggle}
        disabled={busy}
        style={{
          padding: '10px 18px', borderRadius: 10,
          background: pushEnabled ? 'var(--t-sage)' : 'var(--t-bg-elev)',
          border: `1px solid ${pushEnabled ? 'var(--t-sage)' : 'var(--t-line)'}`,
          color: pushEnabled ? 'var(--t-bg)' : 'var(--t-text)',
          fontFamily: 'var(--t-sans)', fontSize: 13, fontWeight: 500,
          cursor: busy ? 'wait' : 'pointer',
          transition: 'all 200ms ease',
        }}
      >{busy ? '…' : pushEnabled ? '🔔 Włączone' : '🔕 Wyłączone'}</button>
      <span style={{
        fontSize: 12, color: 'var(--t-text-dim)',
      }}>
        {pushEnabled
          ? 'Powiadomienia push są aktywne. Otrzymasz alerty o mrozie, końcu karencji i zadaniach.'
          : 'Kliknij aby włączyć powiadomienia push. Przeglądarka poprosi o zgodę.'}
      </span>
    </div>
  );
}

// ── BlockedUsers — lista zablokowanych (S2) ────────────────────
function BlockedUsers() {
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    let mounted = true;
    axios.get('/api/social/block/list')
      .then(r => { if (mounted) setData(r.data); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const unblock = async (id) => {
    try {
      await axios.delete(`/api/social/block/${id}`);
      setData(prev => (prev || []).filter(u => u.id !== id));
      setMsg('Odblokowano');
    } catch { setMsg('Błąd'); }
  };

  if (!data || data.length === 0) {
    return (
      <div style={{ fontSize: 13, color: 'var(--t-text-dim)', padding: '12px 0' }}>
        {data ? 'Brak zablokowanych użytkowników.' : 'Ładowanie…'}
      </div>
    );
  }

  return (
    <div>
      {msg && <div style={{ fontSize: 12, color: 'var(--t-sage)', marginBottom: 8 }}>{msg}</div>}
      {data.map(u => (
        <div key={u.id} style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '8px 0', borderBottom: '1px solid var(--t-line)',
        }}>
          <span style={{ flex: 1, fontSize: 13 }}>{u.displayName}</span>
          <button type="button" onClick={() => unblock(u.id)} style={{
            padding: '4px 10px', borderRadius: 6,
            background: 'transparent', border: '1px solid var(--t-line-2)',
            color: 'var(--t-text-dim)', fontSize: 11, cursor: 'pointer',
          }}>Odblokuj</button>
        </div>
      ))}
    </div>
  );
}

// ── NotificationPrefs — sekcja preferencji maili (M18) ────────────
const NOTIF_TOGGLES = [
  { key: 'notif_frost_alerts',     label: '❄ Alerty mrozowe',
    desc: 'Powiadomienie 24-48h przed prognozowanym przymrozkiem (kiedy okryć rośliny wrażliwe).' },
  { key: 'notif_withdrawal',       label: '⏰ Karencje oprysków',
    desc: 'Powiadomienie gdy mija okres karencji po oprysku — można zbierać.' },
  { key: 'notif_harvest_ready',    label: '🥕 Zbiór gotowy',
    desc: 'Mail gdy roślina osiągnęła expected_harvest_date.' },
  { key: 'notif_task_reminders',   label: '📋 Przypomnienia o zadaniach',
    desc: 'Mail dzień przed terminem zadania.' },
  { key: 'notif_weather_warnings', label: '⛈ Ekstremalna pogoda',
    desc: 'Burza, fala upałów, długa susza, silny wiatr (tylko ekstremalne).' },
  { key: 'notif_weekly_digest',    label: '📰 Raport tygodniowy',
    desc: 'Niedzielne podsumowanie: zadania na nadchodzący tydzień, zbiory, najnowsze plany.' },
];

function NotificationPrefs() {
  const [prefs, setPrefs] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [savedKey, setSavedKey] = React.useState(null);

  React.useEffect(() => {
    let mounted = true;
    axios.get('/api/auth/notifications')
      .then(r => { if (mounted) setPrefs(r.data); })
      .catch(e => { if (mounted) setError(e.response?.data?.error || 'Nie udało się pobrać preferencji.'); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const toggle = async (key) => {
    if (saving || !prefs) return;
    setSaving(true);
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next); // optimistic
    try {
      await axios.put('/api/auth/notifications', { [key]: next[key] });
      setSavedKey(key);
      setTimeout(() => setSavedKey(null), 1800);
    } catch (e) {
      // Rollback
      setPrefs(prefs);
      setError(e.response?.data?.error || 'Nie udało się zapisać.');
      setTimeout(() => setError(''), 4000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ fontSize: 13, color: 'var(--t-text-dim)' }}>Wczytuję preferencje…</div>;
  if (error && !prefs) {
    return (
      <div style={{
        padding: '10px 12px', borderRadius: 8,
        background: 'rgba(192, 106, 74, 0.08)', color: 'var(--t-rust)',
        fontSize: 13,
      }}>{error}</div>
    );
  }
  if (!prefs) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {NOTIF_TOGGLES.map(t => {
        const on = !!prefs[t.key];
        const justSaved = savedKey === t.key;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => toggle(t.key)}
            disabled={saving}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 14,
              padding: '14px 16px', borderRadius: 12,
              background: on ? 'rgba(168, 196, 158, 0.06)' : 'var(--t-bg-elev)',
              border: `1px solid ${on ? 'rgba(168, 196, 158, 0.30)' : 'var(--t-line)'}`,
              color: 'var(--t-text)',
              cursor: saving ? 'wait' : 'pointer',
              textAlign: 'left',
              transition: 'all 200ms ease',
              fontFamily: 'var(--t-sans)',
            }}
          >
            {/* Toggle switch */}
            <div style={{
              width: 36, height: 20, borderRadius: 10,
              background: on ? 'var(--t-sage)' : 'var(--t-line-2)',
              position: 'relative', flexShrink: 0,
              marginTop: 2,
              transition: 'background 200ms ease',
            }}>
              <div style={{
                width: 16, height: 16, borderRadius: '50%',
                background: 'var(--t-bg)',
                position: 'absolute',
                top: 2, left: on ? 18 : 2,
                transition: 'left 200ms ease',
              }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 14, fontWeight: 500,
                color: 'var(--t-text)', letterSpacing: '-.01em',
                marginBottom: 4,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                {t.label}
                {justSaved && (
                  <span style={{
                    fontFamily: 'var(--t-mono)', fontSize: 10,
                    letterSpacing: '.18em', textTransform: 'uppercase',
                    color: 'var(--t-sage)',
                  }}>✓ ZAPISANO</span>
                )}
              </div>
              <div style={{ fontSize: 12, color: 'var(--t-text-dim)', lineHeight: 1.5 }}>
                {t.desc}
              </div>
            </div>
          </button>
        );
      })}

      {error && (
        <div style={{
          padding: '8px 12px', borderRadius: 8,
          background: 'rgba(192, 106, 74, 0.08)', color: 'var(--t-rust)',
          fontSize: 12,
        }}>{error}</div>
      )}

      <div style={{
        marginTop: 6, padding: '10px 12px', borderRadius: 8,
        background: 'rgba(232, 230, 221, 0.025)',
        fontSize: 11, color: 'var(--t-text-mute)', lineHeight: 1.5,
      }}>
        💡 Ustawienia zapisują się od razu po kliknięciu. Maile transakcyjne
        (potwierdzenia, reset hasła, weryfikacja) nie podlegają tym ustawieniom.
      </div>
    </div>
  );
}
